import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { type PropsValue } from 'react-select';

import { type OptionType } from '@hivebrite/design-system/Inputs';

import { getCategories } from 'hb-react/backoffice/components/CategoriesList/requests';
import { makeConfigUrls } from 'hb-react/backoffice/components/CategoriesList/utils/configUrls';
import { CATEGORIES_QUERY_STALE_TIME } from 'hb-react/shared/components/ReactSelect/ReactSelectCategories/constants';
import { categoriesToOptions } from 'hb-react/shared/components/ReactSelect/ReactSelectCategories/utils/categoriesToOptions';

/**
 * ReactSelectAsync value needs to be one or more option objects. If the value provided is already an option object, it will be returned as is.
 * If the value is an id or an array of ids, the categories will be fetched and converted to option objects.
 * @param networkId - Network id
 * @param categorizedType - Categorized type
 * @param topicId - Topic id
 * @param value - Value to fetch the categories for
 * @param enabled - Whether the query should be enabled
 * @returns {Object} - Object containing the categories options, the loading state, and the function to add options to the cache
 */
export const useConvertCategoriesValueToOptions = ({
  networkId,
  categorizedType,
  topicId,
  value,
  enabled = true,
}: {
  categorizedType: string;
  enabled?: boolean;
  networkId: number;
  topicId: number | undefined;
  value?: Array<number> | PropsValue<OptionType> | number | null;
}) => {
  const [categoriesOptionsCache, setCategoriesOptionsCache] = useState<
    Array<OptionType>
  >([]);

  const addOptionsToCache = useCallback((categories: Array<OptionType>) => {
    setCategoriesOptionsCache((prev) => {
      const categoriesToAdd = categories.filter(
        (category) =>
          !prev.some((prevCategory) => prevCategory.value === category.value),
      );

      if (categoriesToAdd.length > 0) {
        return [...prev, ...categoriesToAdd];
      }

      return prev;
    });
  }, []);

  const { isLoading, data: options = [] } = useQuery<PropsValue<OptionType>>({
    queryKey: [networkId, topicId, 'categories', categorizedType, value],
    enabled,
    queryFn: async () => {
      if (value === null || value === undefined) {
        return [];
      }

      let ids: Array<number> = [];

      // initialValue is an array of ids
      if (
        Array.isArray(value) &&
        value.length > 0 &&
        value.every((value) => typeof value === 'number')
      ) {
        ids = value;
      }

      // initialValue is a single id
      if (typeof value === 'number') {
        ids = [value];
      }

      // The parent is storing only the category ids. We need fetch the categories and convert them to options
      if (ids.length > 0) {
        const optionsFromCache = ids
          .map((id) =>
            categoriesOptionsCache.find((category) => category.value === id),
          )
          .filter(Boolean);

        // All the ids where found in the cache
        if (optionsFromCache.length === ids.length) {
          return optionsFromCache;
        }

        // Some of the ids where not found in the cache, we need to fetch the missing ones
        const idsToFetch = ids.filter(
          (id) => !optionsFromCache.some((option) => option.value === id),
        );

        const configUrls = makeConfigUrls({
          networkId,
          type: categorizedType,
          topicId,
          page: 1,
          hasPagination: true,
          perPage: idsToFetch.length,
          ids: idsToFetch,
        });

        const { data } = await getCategories(configUrls);

        // Convert the fetched categories to options
        const fetchedOptions = categoriesToOptions(data);

        addOptionsToCache(fetchedOptions);

        return [...optionsFromCache, ...fetchedOptions];
      }

      return value as Array<OptionType> | OptionType;
    },
    staleTime: CATEGORIES_QUERY_STALE_TIME,
  });

  return {
    isLoading,
    options,
    addOptionsToCache,
  };
};
